/**
 * BudgetRow Component
 * Individual line item row for budget table
 */

import React from 'react';
import { Edit2, TrendingUp } from 'lucide-react';
import type { BudgetLineItem } from '../../types/budget';
import VarianceIndicator from './VarianceIndicator';

export interface BudgetRowProps {
  lineItem: BudgetLineItem;
  onEdit?: (lineItem: BudgetLineItem) => void;
  onForecast?: (lineItem: BudgetLineItem) => void;
  isSelected?: boolean;
}

const BudgetRow: React.FC<BudgetRowProps> = ({ lineItem, onEdit, onForecast, isSelected }) => {
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <tr
      className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${isSelected ? 'bg-brand-50' : ''}`}
    >
      {/* Cost Code */}
      <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
        {lineItem.costCode}
      </td>

      {/* Description */}
      <td className="px-4 py-3 text-sm text-gray-900">
        <div className="max-w-xs">
          <div className="font-medium truncate" title={lineItem.description}>
            {lineItem.description}
          </div>
          {lineItem.category && (
            <div className="text-xs text-gray-500 mt-0.5">{lineItem.category}</div>
          )}
        </div>
      </td>

      {/* Original Budget */}
      <td className="px-4 py-3 text-sm text-gray-700 text-right whitespace-nowrap">
        {formatCurrency(lineItem.originalBudget)}
      </td>

      {/* Approved Changes */}
      <td className="px-4 py-3 text-sm text-gray-700 text-right whitespace-nowrap">
        {lineItem.approvedChanges !== 0 && (
          <span className={lineItem.approvedChanges > 0 ? 'text-green-600' : 'text-red-600'}>
            {lineItem.approvedChanges > 0 ? '+' : ''}
            {formatCurrency(lineItem.approvedChanges)}
          </span>
        )}
        {lineItem.approvedChanges === 0 && <span className="text-gray-400">-</span>}
      </td>

      {/* Revised Budget */}
      <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right whitespace-nowrap">
        {formatCurrency(lineItem.revisedBudget)}
      </td>

      {/* Committed */}
      <td className="px-4 py-3 text-sm text-gray-700 text-right whitespace-nowrap">
        {formatCurrency(lineItem.committed)}
      </td>

      {/* Actual */}
      <td className="px-4 py-3 text-sm text-gray-700 text-right whitespace-nowrap">
        {formatCurrency(lineItem.actual)}
      </td>

      {/* Forecast */}
      <td className="px-4 py-3 text-sm text-gray-700 text-right whitespace-nowrap">
        {formatCurrency(lineItem.forecastAtCompletion)}
      </td>

      {/* Variance */}
      <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
        <VarianceIndicator
          variance={lineItem.variance}
          variancePercent={lineItem.variancePercent}
          size="sm"
        />
      </td>

      {/* % Used */}
      <td className="px-4 py-3 text-sm text-gray-700 text-right whitespace-nowrap">
        <div className="flex items-center justify-end space-x-2">
          <div className="w-16 bg-gray-200 rounded-full h-1.5">
            <div
              className={`h-1.5 rounded-full ${
                lineItem.percentUsed > 100
                  ? 'bg-red-600'
                  : lineItem.percentUsed > 90
                    ? 'bg-yellow-600'
                    : 'bg-green-600'
              }`}
              style={{ width: `${Math.min(lineItem.percentUsed, 100)}%` }}
            />
          </div>
          <span className="text-xs font-medium">{lineItem.percentUsed.toFixed(0)}%</span>
        </div>
      </td>

      {/* Actions */}
      <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
        <div className="flex items-center justify-end space-x-2">
          {onEdit && (
            <button
              onClick={() => onEdit(lineItem)}
              className="p-1.5 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded transition-colors"
              title="Edit line item"
            >
              <Edit2 className="h-4 w-4" />
            </button>
          )}
          {onForecast && (
            <button
              onClick={() => onForecast(lineItem)}
              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
              title="Update forecast"
            >
              <TrendingUp className="h-4 w-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

export default BudgetRow;
